<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { v4 as uuid } from 'uuid'
import { flyCoins } from '../../composables/coinFly'
import { useCoinDisplayHold } from '../../composables/useCoinDisplayHold'
import { claimBonus, fetchBonusCalendar } from '@/composables/diamondCard/service'
import { useRewardsRedDotStore, useToastStore, useUserStore } from '@/stores'
import { t } from '@/i18n/utils'

const REQUEST_ID_KEY = 'dc_pending_claim_request_id'

const { $toast } = useToastStore()
const userStore = useUserStore()
const redDotStore = useRewardsRedDotStore()
const { hold: holdCoinDisplay, release: releaseCoinDisplay } = useCoinDisplayHold()

const calendar = ref<DiamondCardCalendarData | null>(null)
const claiming = ref(false)
// 领取返回 FAILED_RETRYABLE 后置为 true，按钮切换为重试（复用原 requestId）
const retryable = ref(false)

// #region 倒计时：以服务端 remainingSeconds + 单调时钟递减，归零后重新请求
const countdownSeconds = ref(0)
let countdownBase = 0
let countdownStartedAt = 0
let countdownTimer: ReturnType<typeof setInterval> | null = null

function stopCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function startCountdown(remainingSeconds: number) {
  stopCountdown()
  countdownBase = remainingSeconds
  countdownStartedAt = performance.now()
  countdownSeconds.value = remainingSeconds
  countdownTimer = setInterval(() => {
    const elapsed = Math.floor((performance.now() - countdownStartedAt) / 1000)
    countdownSeconds.value = Math.max(0, countdownBase - elapsed)
    // 归零只触发重新请求，资格以服务端为准
    if (countdownSeconds.value <= 0) {
      stopCountdown()
      fetchCalendar()
    }
  }, 1000)
}

const countdownText = computed(() => {
  const s = countdownSeconds.value
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`
})
// #endregion

async function fetchCalendar() {
  try {
    const data = await fetchBonusCalendar()
    calendar.value = data
    retryable.value = data.today?.status === 'FAILED_RETRYABLE'
    if (data.countdown)
      startCountdown(data.countdown.remainingSeconds)
    else
      stopCountdown()
  }
  catch (error: any) {
    console.error(error)
  }
}

const todayStatus = computed<DiamondCardDayStatus | ''>(() => calendar.value?.today?.status ?? '')

const progressPercent = computed(() => {
  const summary = calendar.value?.summary
  if (!summary || !summary.totalBonusCoins)
    return 0
  return Math.min(100, (summary.claimedCoins / summary.totalBonusCoins) * 100)
})

// 周期内每日奖励已全部领完：不再展示领取/倒计时按钮
const allClaimed = computed(() => {
  const summary = calendar.value?.summary
  return !!summary && summary.totalBonusCoins > 0 && summary.claimedCoins >= summary.totalBonusCoins
})

// 按钮态：可点（领取/重试）与禁用文案
const claimable = computed(() => !claiming.value && (todayStatus.value === 'CLAIMABLE' || retryable.value))

const buttonText = computed(() => {
  if (claiming.value || todayStatus.value === 'PROCESSING')
    return t('dcProcessing')
  if (retryable.value || todayStatus.value === 'FAILED_RETRYABLE')
    return t('dcRetry')
  switch (todayStatus.value) {
    case 'CLAIMABLE':
      return t('dcCheckIn')
    case 'CLAIMED':
      return t('dcNextRewardIn', { time: countdownText.value })
    case 'MISSED':
      return t('dcMissedToday')
    case 'FAILED_FINAL':
      return t('dcClaimFailedFinal')
    case 'INVALID':
      return t('dcExpired')
    default:
      return t('dcNextRewardIn', { time: countdownText.value })
  }
})

// #region 领取
// requestId 绑定服务端 benefitDate：跨天残留的 pending id 不能复用，否则服务端会幂等重放前一天的领取
function getOrCreateRequestId() {
  const benefitDate = calendar.value?.today?.benefitDate || calendar.value?.serverDate || ''
  try {
    const stored = JSON.parse(window.localStorage.getItem(REQUEST_ID_KEY) || '')
    if (stored?.date === benefitDate && typeof stored.id === 'string')
      return stored.id
  }
  catch {}
  const id = uuid()
  window.localStorage.setItem(REQUEST_ID_KEY, JSON.stringify({ date: benefitDate, id }))
  return id
}

function clearRequestId() {
  window.localStorage.removeItem(REQUEST_ID_KEY)
}

// 领取成功庆祝层
const congratsVisible = ref(false)
const claimedAmount = ref(0)
const flying = ref(false)
const bigDiamondRef = ref<HTMLImageElement>()

async function onClaim() {
  if (!claimable.value)
    return

  claiming.value = true
  // 余额会先于收取动画更新，冻结顶部显示
  holdCoinDisplay()
  try {
    const result = await claimBonus(getOrCreateRequestId())
    if (result.claimStatus === 'CLAIMED') {
      clearRequestId()
      retryable.value = false
      claimedAmount.value = result.coinAmount
      // 余额以服务端为准，禁止本地累加
      if (result.balanceAfter != null)
        userStore.$state.coinBalance = result.balanceAfter
      else
        await userStore.init({ force: true })
      congratsVisible.value = true
    }
    else if (result.claimStatus === 'PROCESSING') {
      releaseCoinDisplay()
      $toast(t('dcProcessing'))
      fetchCalendar()
    }
    else if (result.claimStatus === 'FAILED_RETRYABLE') {
      releaseCoinDisplay()
      retryable.value = true
      $toast(t('dcClaimFailed'))
    }
    else {
      releaseCoinDisplay()
      clearRequestId()
      retryable.value = false
      $toast(t('dcClaimFailedFinal'))
      fetchCalendar()
    }
  }
  catch (error: any) {
    releaseCoinDisplay()
    console.error(error)
    // 仅业务错误（api.ts reject 的 {code,msg}，code 为数字）且非 5507 领取处理中才清 requestId；
    // 网络/超时等传输错误（AxiosError，code 为字符串）响应结果未知，必须保留原 id 幂等重试，避免重复发放
    if (typeof error?.code === 'number' && error.code !== 5507)
      clearRequestId()
    $toast(error?.msg || t('dcClaimFailed'))
    fetchCalendar()
  }
  finally {
    claiming.value = false
  }
}

async function onCongratsTap() {
  if (flying.value)
    return
  const target = document.querySelector<HTMLElement>('[data-coin-target]')
  try {
    if (bigDiamondRef.value && target) {
      flying.value = true
      await flyCoins({
        from: bigDiamondRef.value,
        to: target,
        icon: '/images/vowpath/welfare/diamond-big.png',
      })
    }
  }
  finally {
    // 动效结束后再放开余额显示，并刷新进度与红点
    releaseCoinDisplay()
    flying.value = false
    congratsVisible.value = false
    fetchCalendar()
    redDotStore.refresh({ force: true })
  }
}
// #endregion

onMounted(fetchCalendar)
onBeforeUnmount(() => {
  stopCountdown()
  releaseCoinDisplay()
})
</script>

<template>
  <div v-if="calendar?.eligible" class="dc-card mx-15px mt-16px relative">
    <!-- 顶部：钻石卡图标 + 标题 + 右侧领取按钮（对照蓝湖「福利」345x113 卡片） -->
    <div class="dc-head flex items-center">
      <img src="/images/vowpath/welfare/dc-card-icon.png" class="dc-icon shrink-0">
      <h3 class="dc-title flex-1 min-w-0 text-18px text-n-text-1 font-bold truncate">
        {{ t('diamondCard') }}
      </h3>
      <a
        v-if="!allClaimed"
        class="dc-btn shrink-0 flex items-center justify-center text-white"
        :class="{ 'dc-btn--disabled': !claimable, 'dc-btn--compact': !claimable }"
        @click="onClaim"
      >
        {{ buttonText }}
      </a>
    </div>
    <p class="dc-slogan text-11px">
      {{ t('diamondCardSlogan') }}
    </p>

    <!-- 领取进度：圆点下方为已领每日奖励钻石，右侧为每日奖励钻石总量（均不含一次性永久钻石） -->
    <div class="dc-progress flex items-center">
      <div class="dc-track relative">
        <div class="dc-fill" :style="{ width: `${progressPercent}%` }" />
        <!-- 尚未领取（进度 0）时不显示圆环及其下方已领数 -->
        <div v-if="progressPercent > 0" class="dc-knob" :style="{ left: `max(${progressPercent}%, 10px)` }" />
        <span v-if="progressPercent > 0" class="dc-knob-label" :style="{ left: `max(${progressPercent}%, 10px)` }">
          {{ calendar.summary?.claimedCoins ?? 0 }}
        </span>
      </div>
      <span class="dc-total">{{ calendar.summary?.totalBonusCoins ?? 0 }}</span>
    </div>

    <!-- 领取成功：全屏庆祝层（与签到成功同款） -->
    <div v-if="congratsVisible" class="congrats-mask" :class="{ 'is-flying': flying }" @click="onCongratsTap">
      <img src="/images/vowpath/welfare/ribbon-congrats.png" class="congrats-ribbon">
      <div class="congrats-glow-wrap">
        <img src="/images/vowpath/welfare/glow.png" class="congrats-glow">
        <img ref="bigDiamondRef" src="/images/vowpath/welfare/diamond-big.png" class="congrats-diamond">
      </div>
      <p class="congrats-count">
        ×{{ claimedAmount }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* 卡片：蓝湖「卡片背景」切图（345x113，含 1px 粉紫描边、71% 透明底、右侧模糊钻石装饰） */
.dc-card {
  height: 113px;
  padding: 16px 17px 0 13px;
  box-sizing: border-box;
  background: url('/images/vowpath/welfare/dc-card-bg.png') no-repeat center / 100% 100%;
}

/* 头部行高度取按钮高度 34，图标/标题垂直居中于 y≈33 */
.dc-head {
  height: 34px;
}

.dc-icon {
  width: 36px;
  height: 23px;
  margin-right: 5px;
}

/* 领取按钮：胶囊，270deg 粉→紫渐变（设计稿 90x34，按反馈收窄为 80x30） */
.dc-btn {
  min-width: 80px;
  height: 30px;
  padding: 0 12px;
  margin-left: 8px;
  border-radius: 20px;
  background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  box-sizing: border-box;

  &.dc-btn--disabled {
    opacity: 0.35;
  }

  /* 倒计时等长文案：数字等宽（宽度不随秒数跳动）+ 限宽后允许折两行，避免挤压标题 */
  &.dc-btn--compact {
    max-width: 120px;
    padding: 0 10px;
    font-size: 10px;
    line-height: 12px;
    white-space: normal;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
}

.dc-slogan {
  margin-top: 5px;
  line-height: 14px;
  color: #cfcfdc;
}

/* 进度行：轨道 234x7 + 右侧总数，行高取圆点 13，轨道中心 y≈89.5 */
.dc-progress {
  margin-top: 14px;
  height: 13px;
}

.dc-track {
  width: 234px;
  height: 7px;
  border-radius: 70px;
  background: rgba(32, 33, 75, 1);
}

/* 填充：奶白 #FFFEDA → 粉 #F26BF1 渐变铺满填充自身，露出多少都是完整渐变 */
.dc-fill {
  height: 100%;
  border-radius: 70px;
  background: linear-gradient(90deg, rgba(255, 254, 218, 1) 0%, rgba(242, 107, 241, 1) 100%);
  transition: width 0.3s ease;
}

/* 圆环：12x12，径向渐变绘制「中心 5px 半透明白点（略小于 7px 轨道，像嵌在轨道里）+ 约 1px 柔边白环」 */
.dc-knob {
  position: absolute;
  top: -2.5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.6) 0,
    rgba(255, 255, 255, 0.6) 2.5px,
    rgba(255, 255, 255, 0) 2.9px,
    rgba(255, 255, 255, 0) 4.2px,
    rgba(255, 255, 255, 0.9) 4.7px,
    rgba(255, 255, 255, 0.9) 5.3px,
    rgba(255, 255, 255, 0) 6px
  );
  /* 圆环右边缘略超出填充末端 2px，主体仍落在填充内 */
  transform: translateX(calc(-100% + 2px));
  transition: left 0.3s ease;
}

/* 圆点下方已领数：10px 灰紫，水平居中于圆点 */
.dc-knob-label {
  position: absolute;
  top: 12px;
  font-size: 10px;
  line-height: 12px;
  color: #b4b5c6;
  white-space: nowrap;
  /* 居中于圆环中心（填充末端 - 4px） */
  transform: translateX(calc(-50% - 4px));
  transition: left 0.3s ease;
}

.dc-total {
  margin-left: 7px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  color: #ffffff;
}

/* 领取成功全屏庆祝层（与签到成功同款） */
.congrats-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background-color 0.4s ease;

  &.is-flying {
    background: rgba(0, 0, 0, 0);
    pointer-events: none;

    .congrats-ribbon,
    .congrats-glow-wrap,
    .congrats-count {
      opacity: 0;
      transform: scale(0.6);
      transition: opacity 0.35s ease, transform 0.35s ease;
    }
  }
}

.congrats-ribbon {
  width: 286px;
  height: auto;
}

.congrats-glow-wrap {
  position: relative;
  width: 214px;
  height: 213px;
  margin-top: 20px;
}

.congrats-glow {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: congrats-spin 6s linear infinite;
}

.congrats-diamond {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 90px;
}

.congrats-count {
  margin-top: -30px;
  color: #ffffff;
  font-size: 24px;
  font-weight: bold;
  position: relative;
  z-index: 1;
}

@keyframes congrats-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
